import { Loc, select } from "gamedata/GameData";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { z } from "zod";
import { DevScreenshotUplodaer } from "~/components/form/DevScreenshotUploader";
import { InputMD } from "~/components/form/InputMd";
import { AdenaValue } from "~/components/item/Adena";
import { LocLink } from "~/components/locs/LocLink";
import { NpcLink } from "~/components/npc/NpcLink";
import { QuestLink } from "~/components/quest/QuestLink";
import { QuestProfList } from "~/components/quest/QuestProfList";
import { QuestRaceList } from "~/components/quest/QuestRaceList";
import { QuestRewardList } from "~/components/quest/QuestRewardList";
import { NotFoundInfo } from "~/components/site/NotFoundInfo";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Facts } from "~/components/ui/Facts";
import { Icon } from "~/components/ui/Icon";
import { InfoBox } from "~/components/ui/InfoBox";
import { PageTitle } from "~/components/ui/PageTitle";
import { Table } from "~/components/ui/Table";
import { Tabs } from "~/components/ui/Tabs";
import { GameVer, tt, useT, useTurl } from "~/util/Lang";
import { $LOADER, $PAGE, OurLoaderArgs } from "~/util/Loaders";
import { SortBy, SortColumnName, createSorting } from "~/util/Sorting";
import { stripSlashes } from "~/util/common";
import { getNpcList } from "~/util/npc";
import { logPageView } from "~/util/stats";
import { homePage } from "~/util/titles";

type LocNpcSort = {
  type: string;
  name: string;
  nick: string;
  npcName: string;
  nickColor: "default" | "quest" | "raid";
  level: number | null;
  race: {
    icon: string;
    name: string;
    raceName: string;
  };
  agro: boolean | null;
  exp: number | null;
};

const Params = z.object({ locId: z.string() });

const SORTER_NPC = createSorting<LocNpcSort>({
  name: (a, b) => (a.name ?? "").localeCompare(b.name ?? ""),
  lvl: (a, b) => (a.level ?? 0) - (b.level ?? 0),
  exp: (a, b) => (a.exp ?? 0) - (b.exp ?? 0),
  race: (a, b) =>
    (a.race?.raceName ?? "").localeCompare(b.race?.raceName ?? ""),
});

type LocAlt = {
  name: string;
  locName: string;
  gver: GameVer;
};

async function loadData(
  la: OurLoaderArgs,
): Promise<
  | { status: "404"; alts: LocAlt[] }
  | { status: "ok"; data: Awaited<ReturnType<typeof makeData>> }
> {
  const t = tt.bind(null, la.realm.lang);
  const params = Params.parse(la.params);
  const loc = la.gd.locs.get(params.locId);

  if (loc == null) {
    la.setStatusCode(404);
    const alts: LocAlt[] = [];
    for (const altGD of Object.values(la.gd.all_gd!)) {
      const altLoc = altGD.locs.get(params.locId);
      if (altLoc) {
        alts.push({
          name: t(altLoc.name),
          locName: altLoc.locName,
          gver: altGD.gver,
        });
      }
    }
    return { status: "404", alts };
  }

  return { status: "ok", data: await makeData(la, loc) };
}

async function makeData(la: OurLoaderArgs, loc: Loc) {
  // Log
  logPageView(la);
  //

  const t = tt.bind(null, la.realm.lang);

  if (loc == null) {
    throw Response.json(
      { msg: t({ en: "Location not found.", ru: "Локация не найдена." }) },
      { status: 404 },
    );
  }

  const data = {
    loc: select(loc, {
      locName: true,
      name: { en: true, ru: true },
      desc: (x) => t({ en: x?.en ?? "", ru: x?.ru ?? "" }),
      levels: { min: true, max: true },
      npcs: {
        select: {
          type: true,
          name: t,
          nick: t,
          npcName: true,
          nickColor: true,
          level: true,
          race: { icon: true, name: t, raceName: true },
          agro: true,
          exp: true,
        },
      },
      dropList: {
        select: {
          item: { name: t, itemName: true, icon: true },
          npcs: {
            select: {
              type: true,
              name: t,
              nick: t,
              nickColor: true,
              npcName: true,
              level: true,
            },
          },
        },
      },
      spoilList: {
        select: {
          item: { name: t, itemName: true, icon: true },
          npcs: {
            select: {
              type: true,
              name: t,
              nick: t,
              npcName: true,
              nickColor: true,
              level: true,
            },
          },
        },
      },
      startsQuests: {
        select: {
          id: true,
          uname: true,
          name: t,
          minLevel: true,
          maxLevel: true,
          reward: { exp: true, sp: true, items: { select: { icon: true } } },
          requirements: {
            category: true,
            items: { select: { select: { name: t } } },
            occupation: {
              select: { id: true, profName: true, name: t },
            },
            races: {
              select: {
                raceName: true,
                icon: true,
                name: t,
              },
            },
          },
        },
      },
      sellList: {
        select: {
          item: { name: t, itemName: true, icon: true },
          price: Number,
          npcs: {
            select: {
              type: true,
              name: t,
              nick: t,
              npcName: true,
              nickColor: true,
              level: true,
            },
          },
        },
      },
      screenshots: { select: { filename: true } },
    }),
  };
  return { ...data, authUser: la.req.sess?.user };
}

$PAGE({
  path: "locs/:locId/*",

  loader: $LOADER?.(async (la) => {
    return loadData(la);
  }),

  meta: (cx) => {
    if (cx.data.status === "404") {
      return {
        title: "Location not found - " + homePage(cx.realm),
      };
    }

    const data = cx.data.data;
    const nameEn = data.loc.name.en;
    const nameRu = data.loc.name.ru;
    const picture = data.loc.screenshots[0];

    const name = cx.t({
      en: `${nameEn}`,
      ru: `${nameRu} (${nameEn})`,
    });

    return {
      title: `${name} - ${cx.t({ en: "Locations", ru: "Локации" })} - ${homePage(
        cx.realm,
      )}`,
      description: `Location ${name}`,
      ogTitle: `Location: ${name}`,
      ogImage: picture && `https://l2hub.info/img/loc/${picture.filename}`,
      canonicalUrl: `/locs/${data.loc.locName}`,
    };
  },

  render: (props) => {
    const loaderData = props.data;
    const params = useParams();
    const t = useT();
    const turl = useTurl();

    const [npcs, setNpcs] = useState<string[]>([]);

    if (loaderData.status === "404") {
      return (
        <NotFoundInfo
          title={t({ en: "Location not found", ru: "Локация не найден" })}
          altsTitle={t({
            en: "This location exists in other gave versions:",
            ru: "Эта локация доступна в других версиях игры:",
          })}
          alts={loaderData.alts.map((altQuest) => ({
            gver: altQuest.gver,
            node: <LocLink loc={altQuest} />,
          }))}
        />
      );
    }

    const data = loaderData.data;

    function sortByNpc(): SortBy {
      return {
        value: npcs,
        update: (next) => setNpcs(next),
      };
    }

    const locsTitle = t({ en: "Locations", ru: "Локации" });
    const breadcrumbList = [
      { title: locsTitle, url: turl("/locs") },
      {
        title: t(data.loc.name),
        url: turl(`/locs/${data.loc.locName}`),
      },
    ];
    const picture = data.loc.screenshots[0];

    return (
      <div>
        <div className="content">
          <Breadcrumb items={breadcrumbList} />
          <div className="columns">
            <div className="column is-three-quarters">
              <PageTitle>{t(data.loc.name)}</PageTitle>
              <div>
                {picture && (
                  <img
                    style={{
                      width: "100%",
                      borderRadius: 3,
                      boxShadow: "0 3px 10px rgb(0 0 0 / 35%)",
                    }}
                    src={`/img/loc/${picture.filename}`}
                    alt={`Location ${data.loc.name} Lineage 2`}
                  />
                )}
                <DevScreenshotUplodaer
                  kind="loc"
                  ident={data.loc.locName}
                  name={t(data.loc.name)}
                />
              </div>
            </div>
            <div className="column">
              <InfoBox
                title={t({
                  en: "Location Facts",
                  ru: "Краткая информация",
                })}
              >
                <Facts
                  facts={[
                    {
                      name: t({ en: "Level", ru: "Уровень" }),
                      value: `${data.loc.levels.min} - ${data.loc.levels.max} `,
                    },
                  ]}
                />
                <InputMD value={`:loc[${data.loc.locName}]`} />
              </InfoBox>
            </div>
          </div>
        </div>
        <Tabs
          current={params["*"] ?? ""}
          link={(id) => turl(stripSlashes(`/locs/${data.loc.locName}/${id}`))}
          tabs={[
            {
              id: "npcs",
              title: t({ en: "NPCs", ru: "НПЦ" }),
              body: () => (
                <Table
                  data={SORTER_NPC.apply(data.loc.npcs, npcs.join(","))}
                  id={(_npc, i) => i}
                  cols={[
                    {
                      title: (
                        <SortColumnName name="name" by={sortByNpc()}>
                          {t({ en: "Name", ru: "Имя" })}
                        </SortColumnName>
                      ),
                      cell: (npc) => (
                        <NpcLink npc={npc} showLvl={false} showIcon={true} />
                      ),
                    },
                    {
                      title: (
                        <SortColumnName name="lvl" by={sortByNpc()}>
                          {t({ en: "Level", ru: "Уровень" })}
                        </SortColumnName>
                      ),
                      cell: (npc) => npc.level,
                    },
                    {
                      title: (
                        <SortColumnName name="exp" by={sortByNpc()}>
                          {t({ en: "Exp", ru: "Опыт" })}
                        </SortColumnName>
                      ),
                      cell: (npc) => (npc.exp ? Math.round(npc.exp) : ""),
                    },
                    {
                      title: t({ en: "Aggr", ru: "Агро" }),
                      cell: (npc) =>
                        (() => {
                          if (npc.agro == null) {
                            return null;
                          }
                          return (
                            <span
                              style={{
                                color: npc.agro ? "red" : "green",
                                fontSize: "12px",
                                fontWeight: 600,
                              }}
                            >
                              {npc.agro
                                ? t({ en: "YES", ru: "ДА" })
                                : t({ en: "NO", ru: "НЕТ" })}
                            </span>
                          );
                        })(),
                    },
                    {
                      title: (
                        <SortColumnName name="race" by={sortByNpc()}>
                          {t({ en: "Race", ru: "Раса" })}
                        </SortColumnName>
                      ),
                      cell: (npc) => npc.race && npc.race.name,
                    },
                  ]}
                />
              ),
            },
            {
              id: "drop",
              title: t({ en: "Drop", ru: "Дроп" }),
              body: () => (
                <Table
                  data={data.loc.dropList}
                  id={(_drop, index) => index}
                  cols={[
                    {
                      title: "",
                      cell: (drop) => (
                        <Icon icon={drop.item.icon} alt={drop.item.name} />
                      ),
                      thPadding: "0",
                      padding: "8px 0 8px 8px",
                      width: 32 + 12,
                    },
                    {
                      title: t({ en: "Name", ru: "Имя" }),
                      cell: (drop) => (
                        <Link to={turl(`/items/${drop.item.itemName}`)}>
                          {drop.item.name}
                        </Link>
                      ),
                    },
                    {
                      title: t({ en: "NPCs", ru: "НПЦ" }),
                      cell: (drop) => <div>{getNpcList(drop.npcs)}</div>,
                      width: "50%",
                    },
                  ]}
                />
              ),
            },
            {
              id: "spoil",
              title: t({ en: "Spoil", ru: "Спойл" }),
              body: () => (
                <Table
                  data={data.loc.spoilList}
                  id={(_spoil, index) => index}
                  cols={[
                    {
                      title: "",
                      cell: (spoil) => (
                        <Icon icon={spoil.item.icon} alt={spoil.item.name} />
                      ),
                      thPadding: "0",
                      padding: "8px 0 8px 8px",
                      width: 32 + 12,
                    },
                    {
                      title: t({ en: "Name", ru: "Имя" }),
                      cell: (spoil) => (
                        <Link to={turl(`/items/${spoil.item.itemName}`)}>
                          {spoil.item.name}
                        </Link>
                      ),
                    },
                    {
                      title: t({ en: "NPCs", ru: "НПЦ" }),
                      cell: (drop) => <div>{getNpcList(drop.npcs)}</div>,
                      width: "50%",
                    },
                  ]}
                />
              ),
            },
            {
              id: "starts",
              title: t({ en: "Starts", ru: "Начинаются" }),
              body: () => (
                <Table
                  data={data.loc.startsQuests}
                  id={(quest) => quest.id}
                  cols={[
                    {
                      title: t({ en: "Name", ru: "Имя" }),
                      cell: (quest) => <QuestLink quest={quest} />,
                    },
                    {
                      title: t({ en: "Level", ru: "Уровень" }),
                      cell: (quest) => (
                        <>
                          {quest.minLevel ?? ""}
                          {quest.maxLevel ? "-" + quest.maxLevel : null}
                        </>
                      ),
                    },
                    {
                      title: t({ en: "Reward", ru: "Награда" }),
                      cell: (quest) => (
                        <QuestRewardList reward={quest.reward} />
                      ),
                    },
                    {
                      title: t({ en: "Race", ru: "Раса" }),
                      cell: (quest) => (
                        <QuestRaceList races={quest.requirements.races} />
                      ),
                    },
                    {
                      title: t({ en: "Class", ru: "Класс" }),
                      cell: (quest) => (
                        <QuestProfList profs={quest.requirements.occupation} />
                      ),
                    },
                  ]}
                />
              ),
            },
            {
              id: "sells",
              title: t({ en: "Sells", ru: "Продаётся" }),
              body: () => (
                <Table
                  data={data.loc.sellList}
                  id={(_sell, index) => index}
                  cols={[
                    {
                      title: "",
                      cell: (sell) => (
                        <Icon icon={sell.item.icon} alt={sell.item.name} />
                      ),
                      thPadding: "0",
                      padding: "8px 0 8px 8px",
                      width: 32 + 12,
                    },
                    {
                      title: t({ en: "Name", ru: "Имя" }),
                      cell: (sell) => (
                        <Link to={turl(`/items/${sell.item.itemName}`)}>
                          {sell.item.name}
                        </Link>
                      ),
                    },
                    {
                      title: t({ en: "NPCs", ru: "НПЦ" }),
                      cell: (sell) => <div>{getNpcList(sell.npcs)}</div>,
                    },
                    {
                      title: t({ en: "Price", ru: "Цена" }),
                      cell: (sell) => <AdenaValue value={sell.price} />,
                    },
                  ]}
                />
              ),
            },
          ]}
        />
      </div>
    );
  },
});
